import { ArrowLeft, Clock3 } from "lucide-react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ProductCard, RegulatedNote, photoBackground, tagLines } from "../components/Cards";
import { usePageHeader } from "../components/shell/PageHeaderContext";
import { Spinner } from "../components/Spinner";
import { money } from "../lib/format";
import { useLibreWallet, useLoanConfig, useMyRequests, useProducts } from "../lib/queries";
import type { Product } from "../types";
import { LoanEntryPage } from "./loan/LoanEntryPage";
import { ApartadosHubPage } from "./apartados/ApartadosHubPage";

/** "Finanzas" (was "Tus ahorros") — the three ways to reach the next destination (Desktop · Productos Hub). */
export function HubPage() {
  usePageHeader("Finanzas", "Tres formas de llegar a tu próximo destino. Elige cómo quieres empezar.");
  const navigate = useNavigate();
  const products = useProducts();
  const { wallet } = useLibreWallet();
  // Reservation requests live in Wallet Aventuria (they become Apartados there), so its card says how many are open.
  const requests = useMyRequests();
  const openRequests = (requests.data?.requests ?? []).filter((request) => request.statusGroup !== "closed").length;
  const requestsNote = openRequests ? `${openRequests} ${openRequests === 1 ? "solicitud en curso" : "solicitudes en curso"}` : null;

  if (products.isLoading) return <div className="page-state"><Spinner label="Cargando productos" /><span>Cargando productos</span></div>;
  const list = products.data?.products ?? [];

  const open = (product: Product) => {
    if (product.id === "libre") navigate(wallet ? "/wallet/libre" : "/wallet/libre/nueva");
    else navigate(`/wallet/${product.id}`);
  };

  return (
    <div className="page-container">
      <div className="product-grid">
        {list.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onClick={() => open(product)}
            meta={product.id === "libre" && wallet
              ? <>Disponible <strong translate="no">{money.format(wallet.balance.free)}</strong>{wallet.goal?.amount ? <> · Meta <strong translate="no">{money.format(wallet.goal.amount)}</strong></> : null}</>
              : product.id === "travel" && (wallet || requestsNote)
                ? <>{wallet?.apartados.length ? <>{wallet.apartados.length} {wallet.apartados.length === 1 ? "Apartado" : "Apartados"} · <strong translate="no">{money.format(wallet.balance.reserved)}</strong></> : requestsNote ? null : "Aparta tu próximo viaje desde tu Wallet Libre"}{requestsNote ? <>{wallet?.apartados.length ? " · " : ""}{requestsNote}</> : null}</>
                : undefined}
          />
        ))}
        {list.length === 0 && <div className="empty-state"><strong>Sin productos publicados</strong><span>El catálogo de productos está vacío.</span></div>}
      </div>
      {/* Finanzas shows the Wallet Libre balance, so it carries the wallet notice (client review, 22 Oct 2026). */}
      <RegulatedNote kind="wallet" />
      <RegulatedNote />
    </div>
  );
}

/**
 * Wallet Aventuria is published in the hub but not yet operable. Travel Now · Pay Later opens the
 * credit application entry when the module is enabled for this account (pilot: administrators).
 */
export function ProductPage() {
  const { productId } = useParams();
  if (productId === "libre") return <Navigate to="/wallet/libre" replace />;
  if (productId === "tnpl") return <TnplProductPage />;
  // "Wallet Aventuria" is the trip wallet: the customer's Apartados over their Wallet Libre.
  if (productId === "travel") return <ApartadosHubPage />;
  return <ComingSoonProductPage />;
}

function TnplProductPage() {
  const config = useLoanConfig();
  if (config.isLoading) return <div className="page-state"><Spinner label="Cargando" /></div>;
  if (config.data?.enabled) return <LoanEntryPage />;
  return <ComingSoonProductPage />;
}

function ComingSoonProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const products = useProducts();
  const product = products.data?.products.find((item) => item.id === productId);
  usePageHeader(product?.title ?? "Producto", product?.eyebrow);
  if (products.isLoading) return <div className="page-state"><Spinner label="Cargando" /></div>;
  if (!product) return <Navigate to="/wallet" replace />;
  return (
    <div className="page-container">
      <section className="coming-hero" style={photoBackground(product.photoUrl, "linear-gradient(160deg, rgba(0,37,87,0.55) 0%, rgba(0,20,45,0.85) 100%)", "wide")}>
        <span className="hero-eyebrow">{product.eyebrow}</span>
        <h2>{product.title}</h2>
        {tagLines(product.tag).map((line) => <p key={line}>{line}</p>)}
        <span className="glass-pill"><em>{product.rateLabel}</em> {product.rateSuffix}</span>
      </section>
      <section className="card coming-card">
        <span className="benefit-icon"><Clock3 size={18} aria-hidden="true" /></span>
        <div>
          <strong>Disponible próximamente</strong>
          <p>Este producto aún no está habilitado para contratación. Mientras tanto, ya puedes ahorrar en tu Wallet Libre y convertirla en un viaje cuando quieras.</p>
        </div>
        <button type="button" className="secondary-button" onClick={() => navigate("/wallet")}><ArrowLeft size={15} aria-hidden="true" /> Volver a Finanzas</button>
      </section>
      <RegulatedNote />
    </div>
  );
}
